/**
 * @fileoverview ViewNotesButton - Componente para mostrar las notas de una orden
 */

import React, { useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Spinner, Alert } from 'reactstrap';
import { NotaRepository } from '../repositories/NotaRepository';
import { Nota, NotaExpanded } from '../types/collections/Nota';
import { createModuleLogger } from '../utils/logger';

// Importar el componente timeline
import NotesTimeline from './NotesTimeline';

interface ViewNotesButtonProps {
  /** Orden de producción */
  orden: {
    id: string;
    numero: number;
    nombre_producto?: string;
    notas?: string[] | null;
  };
  /** Callback opcional cuando se muestran las notas */
  onNotesViewed?: (orderId: string) => void;
}

/**
 * Componente para mostrar un botón que abre un modal con las notas de una orden
 */
export const ViewNotesButton: React.FC<ViewNotesButtonProps> = ({
  orden,
  onNotesViewed,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [notes, setNotes] = useState<NotaExpanded[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const componentLogger = createModuleLogger('ViewNotesButton');

  // Verificar si la orden tiene notas
  const hasNotes = orden.notas && orden.notas.length > 0;

  // Si no tiene notas, no renderizar el botón
  if (!hasNotes) {
    return null;
  }

  const handleOpenModal = async () => {
    try {
      setIsModalOpen(true);
      setLoading(true);
      setError(null);

      componentLogger.info('Abriendo modal de notas', { orderId: orden.id });

      // Crear instancia del repositorio y obtener las notas
      const notaRepository = new NotaRepository();
      const orderNotes = await notaRepository.getNotesByOrderIdExpanded(orden.id);

      setNotes(orderNotes);
      setLoading(false);

      componentLogger.info('Notas cargadas exitosamente', {
        orderId: orden.id,
        notesCount: orderNotes.length,
      });

      // Llamar callback si está definido
      if (onNotesViewed) {
        onNotesViewed(orden.id);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(errorMessage);
      setLoading(false);

      componentLogger.error('Error al cargar notas', {
        orderId: orden.id,
        error: errorMessage,
      });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setNotes([]);
    setError(null);
    setLoading(false);

    componentLogger.info('Modal de notas cerrado', { orderId: orden.id });
  };


  return (
    <>
      <Button
        data-testid="view-notes-btn"
        color="info"
        size="sm"
        className="me-1 px-2 py-1"
        onClick={handleOpenModal}
        title="Ver notas de la orden"
        aria-label="Ver notas de la orden"
      >
        <i className="fas fa-sticky-note me-1" data-testid="notes-icon" />
        Ver Notas ({orden.notas?.length || 0})
      </Button>

      <Modal
        isOpen={isModalOpen}
        toggle={handleCloseModal}
        size="lg"
        centered
        data-testid="notes-modal"
      >
        <ModalHeader toggle={handleCloseModal}>
          <i className="fas fa-sticky-note text-info me-2" />
          Notas de la Orden #{orden.numero}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <h6>Información de la Orden:</h6>
            <ul className="mb-0">
              <li><strong>Número:</strong> #{orden.numero}</li>
              <li><strong>Producto:</strong> {orden.nombre_producto || 'N/A'}</li>
              <li><strong>Total de notas:</strong> {orden.notas?.length || 0}</li>
            </ul>
          </div>

          {loading && (
            <div className="text-center py-4">
              <Spinner color="info" data-testid="loading-spinner" />
              <p className="mt-2 text-muted">Cargando notas...</p>
            </div>
          )}

          {error && (
            <Alert color="danger">
              <i className="fas fa-exclamation-triangle me-2" />
              Error al cargar las notas
            </Alert>
          )}

          {!loading && !error && notes.length === 0 && (
            <Alert color="info">
              <i className="fas fa-info-circle me-2" />
              No hay notas disponibles para esta orden
            </Alert>
          )}

          {!loading && !error && notes.length > 0 && (
            <div>
              <h6>Historial de Notas:</h6>
              <NotesTimeline notes={notes} />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCloseModal}>
            <i className="fas fa-times me-1" />
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ViewNotesButton;
